// One 90-second wall-clock inactivity deadline across the WHOLE Swingies/MiniMove guest flow
// (Home, face gallery, body gallery, edit, result), wired once in App.js EventWrapper.
//
// * ONE deadline per event in sessionStorage["faceFlow:<event>"] (bound to the guest id), set at flow entry
//   and carried across navigation and same-guest reloads: no new 90 s per mounted page, and time spent
//   hidden/in the background counts. An expired flow is latched synchronously (isFlowExpired) so the same
//   dispatch cannot start new work; arriving at Home arms a NEW flow (the expired one is never revived).
//   (the tick compares Date.now(), it never counts ticks).
// * Only REAL activity (capture-phase passive pointerdown / touchstart / keydown) extends it. The
//   expired deadline is checked BEFORE the reset, so a tap after 100 s hidden expires the flow instead of
//   rescuing it. visibilitychange / pageshow only CHECK.
// * Expiry releases the EDITING hold only (best effort, keepalive), clears the flow state and navigates to
//   the validated event Home route (reload is not a Home navigation on body/result routes). On Home itself
//   it just restarts the deadline (Home already starts a fresh guest each visit).
// * Processing is independent: pauseFlowInactivity() (held while an approve is queued/running/reconciling)
//   suspends enforcement; releasing the last pause restarts a full 90 s.
// * Admin preview / non-flow pages: `enabled` false => nothing runs.
// * Display: the face gallery shows the remaining time (useFlowTimeLeft); the later pages show the SAME deadline
//   through FlowCountdown (useFlowCountdown), only while it is enforced and not paused.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFaceClaimEvent } from "./faceClaimEvent";
import {
  clearClaimState,
  clearFlowState,
  currentGuestId,
  flowKey,
  releaseStoredClaim,
} from "./faceClaimClient";

export const FLOW_TIMEOUT_MS = 90000;
const TICK_MS = 1000;
const ACTIVITY_EVENTS = ["pointerdown", "touchstart", "keydown"];

// ---- persisted deadline ------------------------------------------------------------------------------------------

// Stored as {"d": <epoch ms>, "g": <guest id>}: the deadline belongs to the guest that started the flow. A
// reload of the SAME guest keeps it; a record of another guest (new guest deep entry in a tab whose old
// page never ran its cleanup) reads as ABSENT, so the new guest starts its own 90 s.
export const readFlowDeadline = (eventName) => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(flowKey(eventName)));
    if (!parsed || typeof parsed !== "object" || parsed.g !== currentGuestId()) return null;
    const value = Number(parsed.d);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (error) {
    return null;
  }
};

export const writeFlowDeadline = (eventName, deadline) => {
  try {
    sessionStorage.setItem(flowKey(eventName), JSON.stringify({ d: deadline, g: currentGuestId() }));
  } catch (error) {
    // storage unavailable: the guest flow itself (user_id, selections) cannot work either
  }
};

// ---- expired-flow latch (synchronous, shared) ------------------------------------------------------------------
//
// Set in the SAME synchronous step that decides a flow expired (before navigation commits), so a capture-phase
// pointer/key that expires the flow is already visible to the click handler of the same dispatch. Actions
// that start new work (acquire, approve) call isFlowExpired() first. It belongs to one event visit (scope
// OBJECT) and one guest, and is lifted only by arming a NEW flow (arrival at Home / a new guest).
let expiredLatch = null; // { scope, guest }

const latchedFor = (event) =>
  expiredLatch !== null && expiredLatch.scope === event.scope && expiredLatch.guest === currentGuestId();

const latchExpired = (event) => {
  expiredLatch = { scope: event.scope, guest: currentGuestId() };
};

// The same latch for a flow that ended for another observed reason (operator released the hold): no new
// work starts in this visit, and Home arrival arms a new flow exactly as after an expiry.
export const latchFlowEnded = (event) => latchExpired(event);
export const isFlowEnded = (event) => latchedFor(event);

// True when the active visit's flow has expired: latched, or the stored deadline already passed while
// enforcement is not paused (covers an action whose capture listener has not run yet).
export const isFlowExpired = () => {
  const event = getFaceClaimEvent();
  if (!event) return false;
  if (latchedFor(event)) return true;
  if (isFlowPaused()) return false;
  const deadline = readFlowDeadline(event.eventName);
  return deadline !== null && Date.now() >= deadline;
};

// A NEW flow: lift the latch and start a fresh window (never revives the expired deadline).
const armNewFlow = (event) => {
  expiredLatch = null;
  writeFlowDeadline(event.eventName, Date.now() + FLOW_TIMEOUT_MS);
};

// ---- processing pauses (shared across hooks/pages) -----------------------------------------------------------------

let pauseCount = 0;
const pauseListeners = new Set();
// Display-only subscribers (the visible countdown): told on every pause AND release.
const pauseDisplayListeners = new Set();
const notifyPauseDisplay = () => pauseDisplayListeners.forEach((listener) => listener());

export const isFlowPaused = () => pauseCount > 0;

// Returns an idempotent release function. The last release restarts a full timeout window.
export const pauseFlowInactivity = () => {
  pauseCount += 1;
  notifyPauseDisplay();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pauseCount = Math.max(0, pauseCount - 1);
    pauseListeners.forEach((listener) => listener(pauseCount === 0));
    notifyPauseDisplay();
  };
};

// ---- enforced visit (display only) ---------------------------------------------------------------------------------
//
// The event visit whose deadline the hook below is currently enforcing (enabled: running/lite flow event, not admin
// preview, a flow page). The visible countdown shows only for it, so a stored deadline alone never shows a clock.
let enforcedScope = null;

const isFlowEnforced = (event) => enforcedScope !== null && enforcedScope === event.scope;

// ---- the hook --------------------------------------------------------------------------------------------------------------

// enabled: guard (flow event, running, not admin preview, guest page)
// isHome:  the current page is the event Home (expiry restarts instead of navigating)
export const useFlowInactivity = ({ enabled, isHome }) => {
  const navigate = useNavigate();
  const isHomeRef = useRef(isHome);
  isHomeRef.current = isHome;

  useEffect(() => {
    if (!enabled) return undefined;
    const event = getFaceClaimEvent();
    if (!event) return undefined;
    const { eventName } = event;
    let disposed = false;
    enforcedScope = event.scope;

    // sessionStorage is the single source of truth (reload-proof; a cleared key means a new flow).
    const readDeadline = () => readFlowDeadline(eventName);
    const live = () => !disposed && event.isCurrent();

    const restart = () => writeFlowDeadline(eventName, Date.now() + FLOW_TIMEOUT_MS);

    // Synchronous: latches BEFORE navigating, so nothing in the same dispatch can start new work. The
    // latch (not a per-effect flag) is what stops a second expire while the navigation commits; it is
    // lifted only by arming a NEW flow (see the isHome effect below), never by this expired one.
    const expire = () => {
      if (!live() || latchedFor(event)) return;
      // Editing hold only: the server keeps processing/used/blocked claims as they are.
      releaseStoredClaim(event);
      clearClaimState(eventName);
      clearFlowState(eventName);
      if (isHomeRef.current) {
        restart(); // already Home: a fresh visit window, no navigation needed
        return;
      }
      latchExpired(event);
      navigate(event.homeRoute, { replace: true });
    };

    // Flow entry: the first mount of the flow starts the deadline; a reload/remount keeps it. A deadline
    // that already passed (e.g. reload after long hidden time) expires immediately.
    const existing = readDeadline();
    if (latchedFor(event)) {
      // an expired flow is still navigating Home: neither revive it nor start a window
    } else if (existing === null) restart();
    else if (Date.now() >= existing) expire();

    const check = () => {
      if (!live() || isFlowPaused()) return;
      if (latchedFor(event)) return; // expired, waiting for the Home arrival that arms a new flow
      const deadline = readDeadline();
      if (deadline === null) {
        restart(); // storage was cleared / another guest (Home starts a fresh guest): a new flow window
        return;
      }
      if (Date.now() >= deadline) expire();
    };

    const onActivity = () => {
      if (!live() || isFlowPaused()) return;
      if (latchedFor(event)) return; // expired: activity never rescues it
      const deadline = readDeadline();
      // Expiry is decided BEFORE the activity reset.
      if (deadline !== null && Date.now() >= deadline) {
        expire();
        return;
      }
      restart();
    };

    const onResume = () => {
      if (live() && !latchedFor(event)) restart();
    };
    pauseListeners.add(onResume);

    const interval = setInterval(check, TICK_MS);
    const onVisibility = () => check();
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, { capture: true, passive: true })
    );
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onVisibility);

    return () => {
      disposed = true;
      if (enforcedScope === event.scope) enforcedScope = null;
      clearInterval(interval);
      pauseListeners.delete(onResume);
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity, { capture: true })
      );
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onVisibility);
      // Leaving the event (switch / unmount of the gated subtree) ends its flow; a same-event page change
      // never unmounts the EventWrapper that hosts this hook.
      if (!event.isCurrent()) {
        releaseStoredClaim(event);
        clearFlowState(eventName);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Arrival at Home after an expiry (same event: this hook stays mounted) starts a NEW flow: the latch is
  // lifted and a fresh window starts. Other state (event scope, data, other keys) is not touched.
  useEffect(() => {
    if (!enabled || !isHome) return;
    const event = getFaceClaimEvent();
    // (scope match only: Home's fresh-guest start may already have changed the guest id)
    if (event && expiredLatch !== null && expiredLatch.scope === event.scope) armNewFlow(event);
  }, [enabled, isHome]);
};

// Whole seconds left on the shared deadline for the gallery's visible countdown (never mutates it).
export const useFlowTimeLeft = () => {
  const [seconds, setSeconds] = useState(Math.round(FLOW_TIMEOUT_MS / 1000));
  useEffect(() => {
    const event = getFaceClaimEvent();
    if (!event) return undefined;
    const tick = () => {
      const deadline = readFlowDeadline(event.eventName);
      setSeconds(
        deadline === null
          ? Math.round(FLOW_TIMEOUT_MS / 1000)
          : Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      );
    };
    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, []);
  return seconds;
};

// The same shared deadline for the countdown on the pages after the face gallery: whole seconds left, or null
// (nothing to show) when the deadline is not enforced for the active visit (admin preview, not running, other
// pages), the flow already expired/ended, or it is paused (an approve is processing: no clock is running then).
const readCountdown = () => {
  const event = getFaceClaimEvent();
  if (!event || !isFlowEnforced(event) || latchedFor(event) || isFlowPaused()) return null;
  const deadline = readFlowDeadline(event.eventName);
  if (deadline === null) return Math.round(FLOW_TIMEOUT_MS / 1000);
  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
};

export const useFlowCountdown = () => {
  const [seconds, setSeconds] = useState(readCountdown);
  useEffect(() => {
    const tick = () => setSeconds(readCountdown());
    tick();
    const id = setInterval(tick, TICK_MS);
    pauseDisplayListeners.add(tick);
    return () => {
      clearInterval(id);
      pauseDisplayListeners.delete(tick);
    };
  }, []);
  return seconds;
};

export const formatSeconds = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest < 10 ? "0" : ""}${rest}`;
};
